import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin, Star } from 'lucide-react';
import { useSiteData } from '../hooks/useSiteData';
import { BUSINESS, SERVICE_AREAS } from '../lib/site';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { data: settings } = useSiteData('settings');

  const phone = settings?.phone || BUSINESS.phone;
  const email = settings?.email || BUSINESS.email;
  const instagram = settings?.instagram || BUSINESS.instagram;
  const facebook = settings?.facebook || BUSINESS.facebook;
  const review = settings?.google_review || BUSINESS.googleReview;

  return (
    <footer className="bg-[#1A1612] text-[#EADBC8] border-t border-[#C5A880]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">
          <div>
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="Mayden Films" className="h-12 w-auto object-contain mb-4" data-testid="footer-logo-image" />
            ) : (
              <div className="text-2xl font-bold tracking-tight mb-4" data-testid="footer-logo-text">
                <span className="text-[#EADBC8]">Mayden</span>
                <span className="text-[#C5A880] ml-1">Films</span>
              </div>
            )}
            <p className="text-gray-300 text-sm mb-4 leading-relaxed">
              A premium, mobile Glam Booth experience — with selected event photography and videography — for celebrations across Southeast Melbourne.
            </p>
            <div className="flex items-center space-x-4">
              <a href={instagram} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#C5A880] transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
              <a href={facebook} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#C5A880] transition-colors" aria-label="Facebook"><Facebook size={20} /></a>
              <a href={review} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#C5A880] transition-colors flex items-center gap-1 text-sm" aria-label="Leave a Google review" data-testid="footer-google-review">
                <Star size={16} /> Review us
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-[#EADBC8] font-semibold mb-4">Explore</h3>
            <ul className="space-y-2 text-sm">
              {[
                { name: 'Glam Booth', path: '/glam-booth' },
                { name: 'Our Work', path: '/portfolio' },
                { name: 'Photography', path: '/photography' },
                { name: 'Videography', path: '/videography' },
                { name: 'About', path: '/about' },
                { name: 'FAQ', path: '/faq' },
              ].map((l) => (
                <li key={l.path}>
                  <Link to={l.path} className="text-gray-300 hover:text-[#C5A880] transition-colors">{l.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[#EADBC8] font-semibold mb-4">Service Area</h3>
            <ul className="space-y-1.5 text-sm text-gray-300">
              {SERVICE_AREAS.map((area) => (
                <li key={area}>{area}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[#EADBC8] font-semibold mb-4">Get in Touch</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3 text-sm text-gray-300">
                <MapPin size={18} className="text-[#C5A880] mt-0.5 flex-shrink-0" />
                <span>Mobile service based in Cranbourne, Victoria</span>
              </li>
              <li className="flex items-center space-x-3 text-sm text-gray-300">
                <Phone size={18} className="text-[#C5A880] flex-shrink-0" />
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-[#C5A880] transition-colors">{phone}</a>
              </li>
              <li className="flex items-center space-x-3 text-sm text-gray-300">
                <Mail size={18} className="text-[#C5A880] flex-shrink-0" />
                <a href={`mailto:${email}`} className="hover:text-[#C5A880] transition-colors break-all">{email}</a>
              </li>
              <li className="text-xs text-gray-300 pt-1">Enquiries are normally answered within two business days.</li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-[#C5A880]/20">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-300">© {currentYear} Mayden Films. All rights reserved.{settings?.abn ? <span className="text-gray-300"> · ABN {settings.abn}</span> : ''}</p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-gray-300 hover:text-[#C5A880] transition-colors">Privacy Policy</Link>
              <Link to="/booking-terms" className="text-gray-300 hover:text-[#C5A880] transition-colors">Booking Terms</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
